export interface RecordingDiagnosticEvent {
  at: string
  elapsed_ms: number
  type: string
  sequence?: number
  bytes?: number
  last_chunk_at?: string
  gap_ms?: number
  monotonic_gap_ms?: number
  visibility?: DocumentVisibilityState
  recorder_state?: RecordingState
  audio_context_state?: AudioContextState
  track_enabled?: boolean
  track_muted?: boolean
  track_ready_state?: MediaStreamTrackState
  online?: boolean
  peak?: number
}

// Diagnostic callbacks are advisory: never await them on the audio or finalization path.
export function recordingDiagnosticEffect(effect: () => void | Promise<void>) {
  try {
    void Promise.resolve(effect()).catch(() => undefined)
  } catch {
    // Losing diagnostics must not lose audio.
  }
}

export class RecordingDiagnostics {
  private events: RecordingDiagnosticEvent[] = []
  private dropped = 0
  private lastTickAt = Date.now()
  private lastTickMonotonic = performance.now()

  constructor(private report: (event: RecordingDiagnosticEvent) => void | Promise<void>) {}

  start() {
    this.lastTickAt = Date.now()
    this.lastTickMonotonic = performance.now()
  }

  record(event: Omit<RecordingDiagnosticEvent, "at" | "elapsed_ms">) {
    recordingDiagnosticEffect(() => {
      this.events.push({ ...event, at: new Date().toISOString(), elapsed_ms: performance.now() })
      if (this.events.length > 20) {
        this.events.shift()
        this.dropped++
      }
      recordingDiagnosticEffect(() => this.report(this.events[this.events.length - 1]))
    })
  }

  tick(reportGap: (gap: number, monotonicGap: number) => void) {
    const now = Date.now()
    const monotonic = performance.now()
    if (now - this.lastTickAt > 5000 || monotonic - this.lastTickMonotonic > 5000) {
      reportGap(now - this.lastTickAt, monotonic - this.lastTickMonotonic)
    }
    this.lastTickAt = now
    this.lastTickMonotonic = monotonic
  }

  snapshot() {
    return {
      diagnostic_events: this.events.map((event) => ({ ...event })),
      diagnostic_events_dropped: this.dropped,
    }
  }
}
